import React, { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';

import { Alert, Button, Dialog, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';

import stores from '~/consts/stores';
import { UTM_MEDIUM } from '~/consts/utm';

import UsersService from '~/services/UsersService';

import getErrorMessage from '~/utils/getErrorMessage';

type Props = {
  cartId: string;
  user: App.User;
  tenant: App.Tenant;
};

const CALL_CENTRE_LABEL = 'Call centre';

// The channels a quote link can be attributed to. Mirrors the spoof options in
// SpoofUserBlock: the contact centre plus every spoofable store, so a quote
// converted by a store agent is reported against that store rather than
// silently landing on the contact centre.
const SPOOF_CHANNELS: Array<{ label: string; utmMedium: string }> = [
  { label: CALL_CENTRE_LABEL, utmMedium: UTM_MEDIUM },
  ...Object.values(stores)
    .filter((store) => store.spoofable)
    .map((store) => ({ label: store.displayName, utmMedium: store.utmMedium })),
];

export default function QuoteSpoofUser({ cartId, user, tenant }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const [isOpen, setIsOpen] = useState(false);
  const [spoofUrl, setSpoofUrl] = useState<string | null>(null);
  const [channelLabel, setChannelLabel] = useState<string | null>(null);
  const [isProcessingSpoofing, setIsProcessingSpoofing] = useState(false);

  const flushSpoofingState = useCallback(() => {
    setIsOpen(false);
    setSpoofUrl(null);
    setChannelLabel(null);
  }, []);

  const handleSpoofQuote = useCallback(
    async (utmMedium: string, label: string) => {
      try {
        setIsProcessingSpoofing(true);

        const spoofUrl = await UsersService.spoofUserQuote(user.id_member, tenant, cartId, utmMedium);
        navigator.clipboard.writeText(spoofUrl);

        setSpoofUrl(spoofUrl);
        setChannelLabel(label);
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error, 'Failed to generate spoof link.'), { variant: 'error' });
      } finally {
        setIsProcessingSpoofing(false);
      }
    },
    [cartId, enqueueSnackbar, tenant, user],
  );

  return (
    <>
      <Button variant="text" size="small" onClick={() => setIsOpen(true)}>
        View quote
      </Button>

      {isOpen && (
        <Dialog open onClose={flushSpoofingState}>
          <DialogTitle>View quote as</DialogTitle>
          <DialogContent>
            {spoofUrl ? (
              <Stack direction="column" spacing={2} sx={{ my: 2 }}>
                <Alert severity="warning" onClose={flushSpoofingState}>
                  Extreme caution, paste into incognito only
                </Alert>
                <Alert severity="info" onClose={flushSpoofingState}>
                  Your link has been copied to the clipboard!
                </Alert>
                <TextField
                  InputLabelProps={{ shrink: true }}
                  // user.email is empty when this renders from the Agent
                  // Dashboard, which only has the customer id to hand.
                  label={`${channelLabel} quote link${user.email ? ` for ${user.email}` : ''}`}
                  defaultValue={spoofUrl}
                  multiline
                  disabled
                />
              </Stack>
            ) : (
              <Stack direction="column" spacing={2} sx={{ my: 2 }}>
                <Alert severity="info">
                  The channel you pick attributes any resulting order to that sales channel.
                </Alert>
                {SPOOF_CHANNELS.map((channel) => (
                  <Button
                    key={channel.utmMedium}
                    variant="contained"
                    color={channel.label === CALL_CENTRE_LABEL ? 'success' : 'secondary'}
                    onClick={() => handleSpoofQuote(channel.utmMedium, channel.label)}
                    disabled={isProcessingSpoofing}
                  >
                    {channel.label}
                  </Button>
                ))}
              </Stack>
            )}
          </DialogContent>
        </Dialog>
      )}
    </>
  );
}
